const ROOM_CAM_COMPONENT_IDS = new Set(['livekitTrackRoomCam', 'livekitTrackRoomCamGrid']);

/** Broadcast both / camera-only inserts this slot when no stage camera is staged. It never publishes. */
export function isSyntheticRoomCamPlaceholder(itemId: string | undefined): boolean {
  return typeof itemId === 'string' && itemId.startsWith('roomCam:') && itemId.includes('placeholder');
}

export function isRoomCamPreviewComponent(componentId: string): boolean {
  return ROOM_CAM_COMPONENT_IDS.has(componentId);
}

const BROADCAST_PLACEHOLDER_CAM_ID = 'roomCam:simulation-placeholder-cam1';

const CUE_CONTENT_ITEM_PREFIXES = ['media:', 'composite:', 'virtualMedia:', 'screenshare:', 'mediaAgent:'];

function isVisibleCueContent(layer: { visible?: boolean; preload?: boolean; itemId?: string }): boolean {
  const { itemId } = layer;
  return (
    layer.visible !== false &&
    layer.preload !== true &&
    typeof itemId === 'string' &&
    CUE_CONTENT_ITEM_PREFIXES.some((prefix) => itemId.startsWith(prefix))
  );
}

/** Broadcast both / camera-only still needs a tile when the live layout omitted the camera. */
export function withBroadcastCameraPlaceholder<
  T extends {
    layerId: number;
    componentId: string;
    visible?: boolean;
    itemId?: string;
    position: { x: number; y: number; w: number; h: number };
    data: Record<string, string>;
  },
>(layers: T[], showMode: string | undefined, tab: string): T[] {
  if (tab !== 'live' || (showMode !== 'both' && showMode !== 'mixedView' && showMode !== 'speakerOnly')) {
    return layers;
  }
  if (layers.some((layer) => layer.visible !== false && isRoomCamPreviewComponent(layer.componentId))) {
    return layers;
  }
  const fullFrame = showMode === 'speakerOnly';
  // Camera-only content the players still show (e.g. a composite) must not sit under a full-frame fake camera.
  if (fullFrame && layers.some(isVisibleCueContent)) {
    return layers;
  }
  return [
    ...layers,
    {
      layerId: 9901,
      componentId: 'livekitTrackRoomCam',
      itemId: BROADCAST_PLACEHOLDER_CAM_ID,
      visible: true,
      position: fullFrame ? { x: 0, y: 0, w: 100, h: 100 } : { x: 70, y: 4, w: 26, h: 26 },
      data: {},
    } as unknown as T,
  ];
}

/**
 * The camera tile should say the track is unavailable instead of spinning.
 * A synthetic placeholder is absent as soon as the room is up. A real room cam
 * waits out the publication window first, so a camera that is still joining can paint.
 */
export function roomCamTileIsUnavailable(input: {
  componentId: string;
  itemId?: string;
  hasDisplayTrack: boolean;
  roomConnected: boolean;
  publicationWaitElapsed: boolean;
}): boolean {
  if (input.hasDisplayTrack || !input.roomConnected || !isRoomCamPreviewComponent(input.componentId)) {
    return false;
  }
  if (isSyntheticRoomCamPlaceholder(input.itemId)) {
    return true;
  }
  return input.publicationWaitElapsed;
}
